import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowUpRight, Terminal, ShieldCheck } from "lucide-react";
import { Eyebrow } from "./primitives";

import { useMotion } from "./motion";
import EventStream from "./EventStream";

const LINES = ["Every event,", "every endpoint,", "delivered."];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
    const ref = useRef(null);
    const { reduced } = useMotion();
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useTransform(my, [0, 1], [5, -5]);
    const rotateY = useTransform(mx, [0, 1], [-6, 6]);
    const chipY = useTransform(my, [0, 1], [-14, 14]);

    const onMove = (e) => {
        if (reduced) return;
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };

    return (
        <section id="top" data-testid="hero-section" className="relative overflow-hidden">
            <div className="bg-grid-slate absolute inset-0" aria-hidden="true" />
            <div className="glow-hero absolute inset-0" aria-hidden="true" />
            <div
                className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B0F17]"
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-32 sm:pt-36 lg:px-8 lg:pb-24 lg:pt-40">
                <div className="grid items-center gap-14 lg:grid-cols-[1.02fr_0.98fr] lg:gap-10">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE }}
                        >
                            <Link to="/changelog" className="inline-block transition-opacity hover:opacity-80">
                                <Eyebrow data-testid="hero-eyebrow">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                                    v2.4 — Instant Replay is live
                                </Eyebrow>
                            </Link>
                        </motion.div>

                        <h1
                            data-testid="hero-headline"
                            className="font-hero mt-7 text-4xl font-bold leading-[1.04] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
                        >
                            {LINES.map((line, i) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className={`block ${
                                            i === 2
                                                ? "bg-gradient-to-r from-cyan-300 via-cyan-400 to-teal-300 bg-clip-text text-transparent"
                                                : ""
                                        }`}
                                        initial={{ y: "115%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.p
                            data-testid="hero-subcopy"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
                            className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
                        >
                            MDH is the command center for outbound webhooks and streaming events —
                            guaranteed delivery, end-to-end cryptographic trust, and total
                            observability. Wire it up in three lines of code.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.78, ease: EASE }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <a
                                href="https://mdhapi.com/signup"
                                data-testid="hero-cta-start-building"
                                className="group flex items-center gap-2 rounded-md bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-[0_0_24px_rgba(6,182,212,0.4)] transition-all hover:bg-cyan-300 hover:shadow-[0_0_36px_rgba(6,182,212,0.6)]"
                            >
                                Start Building
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                            <Link
                                to="/docs"
                                data-testid="hero-cta-try-api"
                                className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/60 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                            >
                                <Terminal className="h-4 w-4 text-cyan-400" />
                                Try the API
                            </Link>
                        </motion.div>

                        <motion.dl
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1 }}
                            className="mt-10 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-800/80 pt-6"
                        >
                            {[
                                ["12.8B", "events / month"],
                                ["99.999%", "delivery SLA"],
                                ["<12ms", "P99 dispatch"],
                            ].map(([v, l]) => (
                                <div key={l}>
                                    <dt className="sr-only">{l}</dt>
                                    <dd className="font-display text-xl font-bold text-slate-100 sm:text-2xl">
                                        {v}
                                    </dd>
                                    <dd className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                        {l}
                                    </dd>
                                </div>
                            ))}
                        </motion.dl>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5, ease: EASE }}
                        className="relative"
                        style={{ perspective: 1200 }}
                    >
                        <div
                            ref={ref}
                            onMouseMove={onMove}
                            onMouseLeave={() => {
                                mx.set(0.5);
                                my.set(0.5);
                            }}
                            className="relative"
                        >
                            <motion.div
                                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                                className="glass-panel relative rounded-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7),0_0_40px_rgba(6,182,212,0.08)]"
                            >
                                <EventStream compact testid="hero-event-stream" />
                            </motion.div>

                            <motion.div
                                style={{ y: chipY }}
                                data-testid="hero-floating-chip"
                                className="glass-panel absolute -left-4 -top-6 hidden items-center gap-2.5 rounded-lg px-3.5 py-2.5 shadow-xl sm:flex"
                            >
                                <ShieldCheck className="h-4 w-4 text-teal-400" />
                                <div className="font-mono text-[10px] leading-tight">
                                    <div className="text-slate-300">signature verified</div>
                                    <div className="text-slate-500">sha256 · key rotated 2d ago</div>
                                </div>
                            </motion.div>

                            <div
                                className="absolute -bottom-6 -right-4 hidden rounded-lg border border-teal-500/30 bg-teal-950/60 px-3.5 py-2 font-mono text-[10px] text-teal-300 backdrop-blur-md sm:block"
                                data-testid="hero-floating-stat"
                            >
                                4,182 endpoints healthy
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
